import { PaymentRequisiteModel, PaymentRequisiteToType } from '@/entities/paymentRequisit'
import {
  formatBankAccount,
  formatCardNumber,
  formatPhoneNumber,
  RequisiteModel,
} from '@/features/requisites/model'
import { BANK_REQUISITE_CONFIG, REQUISITE_TYPES } from '@/features/requisites/table/model/configs'
import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { UseFormSetValue, UseFormWatch } from 'react-hook-form'

// Интерфейсы
interface Card {
  id: string
  value: string
  requisitType: PaymentRequisiteToType
}

interface FieldError {
  message?: string
}

interface FormState {
  selectedBankId: string
  showAdditionalFields: boolean
  phone: string
  account: string
  mainCard: string
  additionalCards: Card[]
  sbpPaymentLink: string
  errors: {
    phone?: FieldError
    account?: FieldError
    mainCard?: FieldError
    additionalCards: (FieldError | undefined)[]
  }
}

// Actions для reducer
type FormAction =
  | { type: 'INITIALIZE_FORM'; bankId: string }
  | { type: 'RESET_FORM' }
  | { type: 'SET_PHONE'; value: string }
  | { type: 'SET_ACCOUNT'; value: string }
  | { type: 'SET_MAIN_CARD'; value: string }
  | { type: 'SET_ADDITIONAL_CARD'; index: number; value: string }
  | { type: 'SET_SBP_PAYMENT_LINK'; value: string }
  | { type: 'ADD_CARD'; card: Card }
  | { type: 'REMOVE_CARD'; index: number }
  | { type: 'SET_PHONE_ERROR'; error?: FieldError }
  | { type: 'SET_ACCOUNT_ERROR'; error?: FieldError }
  | { type: 'SET_MAIN_CARD_ERROR'; error?: FieldError }
  | { type: 'SET_ADDITIONAL_CARD_ERROR'; index: number; error?: FieldError }
  | { type: 'SET_SHOW_ADDITIONAL_FIELDS'; show: boolean }

// Начальное состояние
const initialState: FormState = {
  selectedBankId: '',
  showAdditionalFields: false,
  phone: '+7',
  account: '',
  mainCard: '',
  additionalCards: [],
  sbpPaymentLink: '',
  errors: {
    phone: undefined,
    account: undefined,
    mainCard: undefined,
    additionalCards: [],
  },
}

// Reducer для управления состоянием
function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'INITIALIZE_FORM':
      return {
        ...initialState,
        selectedBankId: action.bankId,
        showAdditionalFields: true,
        phone: '+7',
        errors: {
          ...initialState.errors,
        },
      }

    case 'RESET_FORM':
      return initialState

    case 'SET_PHONE':
      return {
        ...state,
        phone: action.value,
      }

    case 'SET_ACCOUNT':
      return {
        ...state,
        account: action.value,
      }

    case 'SET_MAIN_CARD':
      return {
        ...state,
        mainCard: action.value,
      }

    case 'SET_ADDITIONAL_CARD':
      return {
        ...state,
        additionalCards: state.additionalCards.map((card, index) =>
          index === action.index ? { ...card, value: action.value } : card
        ),
      }

    case 'SET_SBP_PAYMENT_LINK':
      return {
        ...state,
        sbpPaymentLink: action.value,
      }

    case 'ADD_CARD':
      return {
        ...state,
        additionalCards: [...state.additionalCards, action.card],
        errors: {
          ...state.errors,
          additionalCards: [...state.errors.additionalCards, undefined],
        },
      }

    case 'REMOVE_CARD':
      return {
        ...state,
        additionalCards: state.additionalCards.filter((_, index) => index !== action.index),
        errors: {
          ...state.errors,
          additionalCards: state.errors.additionalCards.filter((_, index) => index !== action.index),
        },
      }

    case 'SET_PHONE_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          phone: action.error,
        },
      }

    case 'SET_ACCOUNT_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          account: action.error,
        },
      }

    case 'SET_MAIN_CARD_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          mainCard: action.error,
        },
      }

    case 'SET_ADDITIONAL_CARD_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          additionalCards: state.errors.additionalCards.map((error, index) =>
            index === action.index ? action.error : error
          ),
        },
      }

    case 'SET_SHOW_ADDITIONAL_FIELDS':
      return {
        ...state,
        showAdditionalFields: action.show,
      }

    default:
      return state
  }
}

interface UseEditRequisiteFormProps {
  watch: UseFormWatch<RequisiteModel>
  setValue: UseFormSetValue<RequisiteModel>
}

export const useEditRequisiteForm = ({ watch, setValue }: UseEditRequisiteFormProps) => {
  const [state, dispatch] = useReducer(formReducer, initialState)

  // Получаем текущую конфигурацию реквизитов для выбранного банка
  const bankConfig = useMemo(() => {
    return BANK_REQUISITE_CONFIG[state.selectedBankId] || BANK_REQUISITE_CONFIG.default
  }, [state.selectedBankId])

  // Получение типов реквизитов по типу банка
  const requisiteTypes = useMemo(() => ({
    sbpType: bankConfig.sbp,
    accountType: bankConfig.account,
    cardType: bankConfig.card,
    sbpLinkType: bankConfig.sbpLink,
    configs: REQUISITE_TYPES,
  }), [bankConfig])

  // Отслеживаем изменение bankId
  const bankId = watch('bankId')

  useEffect(() => {
    if (bankId && bankId !== state.selectedBankId) {
      dispatch({ type: 'INITIALIZE_FORM', bankId })
    } else if (!bankId) {
      dispatch({ type: 'SET_SHOW_ADDITIONAL_FIELDS', show: false })
    }
  }, [bankId, state.selectedBankId])

  // Валидация поля
  const validateField = useCallback((field: 'phone' | 'account' | 'card', value: string) => {
    switch (field) {
      case 'phone': {
        const error = REQUISITE_TYPES[PaymentRequisiteToType.SbpPhoneNumber].validateFunc(value)
        return error ? { message: error } : undefined
      }
      case 'account': {
        const error = REQUISITE_TYPES[PaymentRequisiteToType.AccountNumber].validateFunc(value)
        return error ? { message: error } : undefined
      }
      case 'card': {
        const error = REQUISITE_TYPES[PaymentRequisiteToType.CardNumber].validateFunc(value)
        return error ? { message: error } : undefined
      }
      default:
        return undefined
    }
  }, [])

  // Обновление значений в форме React Hook Form
  const updateFormValue = useCallback((
    index: number,
    value: string,
    requisitType: PaymentRequisiteToType
  ) => {
    setValue(`paymentRequisitProfiles.${index}.value`, value)
    setValue(`paymentRequisitProfiles.${index}.requisitType`, requisitType)
  }, [setValue])

  // Обработчик изменения номера телефона
  const handlePhoneChange = useCallback((value: string) => {
    const { formatted, cleaned } = formatPhoneNumber(value)
    dispatch({ type: 'SET_PHONE', value: formatted })

    const error = validateField('phone', cleaned)
    dispatch({ type: 'SET_PHONE_ERROR', error })

  }, [bankConfig.sbp, updateFormValue, validateField])

  // Обработчик изменения номера счета
  const handleAccountChange = useCallback((value: string) => {
    const { cleaned } = formatBankAccount(value)
    dispatch({ type: 'SET_ACCOUNT', value: cleaned })

    const error = validateField('account', cleaned)
    dispatch({ type: 'SET_ACCOUNT_ERROR', error })

  }, [bankConfig.account, updateFormValue, validateField])

  // Обработчик изменения основной карты
  const handleMainCardChange = useCallback((value: string) => {
    const { cleaned } = formatCardNumber(value)
    dispatch({ type: 'SET_MAIN_CARD', value: cleaned })

    const error = validateField('card', cleaned)
    dispatch({ type: 'SET_MAIN_CARD_ERROR', error })

  }, [bankConfig.sbp, updateFormValue, validateField])

  // Обработчик изменения дополнительной карты
  const handleAdditionalCardChange = useCallback((value: string, cardIndex: number) => {
    const { cleaned } = formatCardNumber(value)
    dispatch({ type: 'SET_ADDITIONAL_CARD', index: cardIndex, value: cleaned })

    const error = validateField('card', cleaned)
    dispatch({ type: 'SET_ADDITIONAL_CARD_ERROR', index: cardIndex, error })
  }, [bankConfig, updateFormValue, validateField])

  // Обработчик изменения sbpPaymentLink
  const handleSbpPaymentLinkChange = useCallback((value: string) => {
    dispatch({ type: 'SET_SBP_PAYMENT_LINK', value })
  }, [])

  // Добавление дополнительной карты
  const addCard = useCallback((initialValue = '', id = '') => {
    const newCard: Card = {
      id: id || `temp-${Date.now()}`,
      value: initialValue,
      requisitType: PaymentRequisiteToType.CardNumber,
    }

    dispatch({ type: 'ADD_CARD', card: newCard })

  }, [bankConfig, state.additionalCards.length, updateFormValue])

  // Удаление дополнительной карты
  const removeCard = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_CARD', index })
  }, [])

  // Инициализация формы в зависимости от выбранного банка
  const initializeForm = useCallback((bankId: string) => {
    dispatch({ type: 'INITIALIZE_FORM', bankId })
  }, [])

  // Валидация всей формы
  const validateForm = useCallback(() => {
    let isValid = true

    // Проверяем основной реквизит
    if (bankConfig.sbp === PaymentRequisiteToType.SbpPhoneNumber) {
      const cleanedPhone = formatPhoneNumber(state.phone).cleaned
      if (!cleanedPhone || cleanedPhone.length < 11) {
        dispatch({
          type: 'SET_PHONE_ERROR',
          error: { message: 'Введите корректный номер телефона' }
        })
        isValid = false
      }
    }

    // Проверяем дополнительные карты
    state.additionalCards.forEach((card, index) => {
      if (card.value.trim()) {
        const cleanedCard = formatCardNumber(card.value).cleaned
        if (cleanedCard.length < 16) {
          dispatch({
            type: 'SET_ADDITIONAL_CARD_ERROR',
            index,
            error: { message: 'Необходимо указать полный номер карты' },
          })
          isValid = false
        }
      }
    })

    return isValid
  }, [bankConfig, state.mainCard, state.phone, state.account, state.additionalCards])

  // Получение настроенных реквизитов
  const getPaymentRequisitProfiles = useCallback((): PaymentRequisiteModel[] => {
    const existingProfiles = watch('paymentRequisitProfiles') || []

    // индексы существующих для сохранения id (по типу+значению)
    const byTypeValue = new Map<string, PaymentRequisiteModel>();
    for (const p of existingProfiles) {
      if (p?.requisitType && p?.value) {
        byTypeValue.set(`${p.requisitType}|${p.value}`, p);
      }
    }

    const drafts: Array<Pick<PaymentRequisiteModel, 'requisitType'|'value'> & { id?: string }> = [];

    // Телефон
    {
      const { cleaned } = formatPhoneNumber(state.phone);
      if (cleaned && cleaned !== '+7') {
        drafts.push({ requisitType: PaymentRequisiteToType.SbpPhoneNumber, value: cleaned });
      }
    }

    // Счет
    {
      const acc = (state.account ?? '').trim();
      if (acc) {
        drafts.push({ requisitType: PaymentRequisiteToType.AccountNumber, value: acc });
      }
    }

    // Главная карта
    {
      const card = (state.mainCard ?? '').trim();
      if (card) {
        drafts.push({ requisitType: PaymentRequisiteToType.CardNumber, value: card });
      }
    }

    // Доп. карты
    for (const c of state.additionalCards) {
      const v = (c.value ?? '').trim();
      if (!v) continue;
      drafts.push({
        requisitType: PaymentRequisiteToType.CardNumber,
        value: v,
        // если id реальный — сохраним его; temp-… опускаем, это новый профиль
        id: c.id && !c.id.startsWith('temp-') ? c.id : undefined,
      });
    }

    const result: PaymentRequisiteModel[] = drafts.map(d => {
      const key = `${d.requisitType}|${d.value}`;
      const matched = byTypeValue.get(key);
      if (matched) return { ...matched, value: d.value, requisitType: d.requisitType };
      if (d.id)    return { id: d.id, requisitType: d.requisitType, value: d.value } as PaymentRequisiteModel;
      return { requisitType: d.requisitType, value: d.value } as PaymentRequisiteModel;
    });

    return result
  }, [watch, bankConfig, state.phone, state.mainCard, state.account, state.additionalCards])

  // Сброс формы
  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
  }, [])

  return {
    // Состояние
    selectedBankId: state.selectedBankId,
    showAdditionalFields: state.showAdditionalFields,
    phone: state.phone,
    account: state.account,
    mainCard: state.mainCard,
    additionalCards: state.additionalCards,
    sbpPaymentLink: state.sbpPaymentLink,

    // Форматированные значения для отображения
    formattedPhone: state.phone,
    formattedAccount: state.account,
    formattedMainCard: formatCardNumber(state.mainCard).formatted,

    // Ошибки
    phoneError: state.errors.phone?.message,
    accountError: state.errors.account?.message,
    mainCardError: state.errors.mainCard?.message,
    additionalCardsErrors: state.errors.additionalCards.map(error => error?.message),

    // Обработчики
    handlePhoneChange,
    handleAccountChange,
    handleMainCardChange,
    handleAdditionalCardChange,
    handleSbpPaymentLinkChange,
    addCard,
    removeCard,

    // Утилиты
    validateForm,
    resetForm,
    initializeForm,
    getPaymentRequisitProfiles,

    // Дополнительные утилиты
    bankConfig,
    requisiteTypes,
  }
}